import { linkOptions, useNavigate, useSearch } from '@tanstack/react-router';

import { PortMessage } from '../../../../../types/PortMessage';
import { CenteredPagination } from '../../../../cdl/Pagination/CenteredPagination';
import { TableBuilder } from '../../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../../cdl/TableBuilder/TableBuilderColumn';
import { AdvancedError } from '../../../../common/Error/AdvancedError';
import { translate } from '../../../../common/helpers/translate.helper';
import { IconList } from '../../../../common/icons/cdl/List';
import { LoadingIndicator } from '../../../../common/LoadingIndicator/LoadingIndicator';
import { Section } from '../../../../common/Section/Section';
import { TableEmptyState } from '../../../../common/TableEmptyState/TableEmptyState';
import { formatPortAliases } from '../../formatPortAliases';
import { usePortPagination } from '../hooks/usePortPagination';

export const PortsOverviewTable = () => {
    const search = useSearch({ from: '/_app/ports' });
    const navigate = useNavigate({ from: '/ports' });

    const portsPaginationQuery = usePortPagination(search.page, search.searchQuery);

    if (portsPaginationQuery.isPending) {
        return (
            <Section>
                <LoadingIndicator />
            </Section>
        );
    }

    if (portsPaginationQuery.isError) {
        return <AdvancedError />;
    }

    if (!portsPaginationQuery.data?.content.length) {
        return (
            <Section>
                <TableEmptyState Icon={IconList} text={translate('port.emptyList')} />
            </Section>
        );
    }

    return (
        <div>
            <TableBuilder<PortMessage>
                data={portsPaginationQuery.data.content}
                rowLink={(row) => {
                    return linkOptions({ to: '/port/detail/$id', params: { id: row.id } });
                }}
                grow={false}
            >
                <TableBuilderColumn<PortMessage> width="20%" header={translate('port.name')} truncate>
                    {(port) => port.name}
                </TableBuilderColumn>
                <TableBuilderColumn<PortMessage> width="15%" header={translate('port.locode')}>
                    {(port) => port.locCode}
                </TableBuilderColumn>
                <TableBuilderColumn<PortMessage> width="20%" header={translate('port.country')} truncate>
                    {(port) => port.country.name}
                </TableBuilderColumn>
                <TableBuilderColumn<PortMessage> width="20%" header={translate('port.aliases')} truncate>
                    {(port) => formatPortAliases(port.aliases)}
                </TableBuilderColumn>
                <TableBuilderColumn<PortMessage> width="25%" header={translate('port.locCodeAliases')} truncate>
                    {(port) => formatPortAliases(port.locCodeAliases)}
                </TableBuilderColumn>
            </TableBuilder>

            <CenteredPagination
                currentPage={search.page}
                pageTotal={portsPaginationQuery.pageTotal}
                onPageChange={(page) =>
                    navigate({
                        search: (previousSearch) => ({ ...previousSearch, page }),
                    })
                }
            />
        </div>
    );
};
